import { FormEvent, useEffect, useMemo, useState } from "react";
import Modal from "../ui/Modal";
import SelectField from "../controls/SelectField";
import { SCompanyRow } from "../../types/companies";
import {
  CompetitorCreateInput,
  CompetitorOut,
  PlatformEnum,
} from "../../types/competitors";
import { listCompanyHotels } from "../../api/hotels";
import { createCompetitor } from "../../api/competitors";
import { OtaKey } from "../../types/ota";
import OtaUrlsFields, {
  buildOtaUrlsPayload,
  countFilledOtaUrls,
  EMPTY_OTA_VALUES,
  getInvalidOtaKeys,
  OtaValues,
} from "../ota/OtaUrlsFields";
import {
  Building2,
  BedDouble,
  MapPin,
  Star,
  BarChart,
  Hash,
  ClipboardList,
  UsersRound,
} from "lucide-react";

const PLATFORM_OPTIONS: { label: string; value: PlatformEnum }[] = [
  { label: "Броневик", value: "Броневик" },
  { label: "Яндекс", value: "Яндекс" },
  { label: "Островок", value: "Островок" },
  { label: "Другое", value: "other" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  companies: SCompanyRow[];
  defaultCompanyId?: number | null;
  defaultHotelId?: number | null;
  onCreated: (competitor: CompetitorOut) => void;
};

type HotelOption = { label: string; value: string };

export default function AddCompetitorModal({
  open,
  onClose,
  companies,
  defaultCompanyId,
  defaultHotelId,
  onCreated,
}: Props) {
  const [companyId, setCompanyId] = useState(defaultCompanyId ? String(defaultCompanyId) : "");
  const [hotelId, setHotelId] = useState(defaultHotelId ? String(defaultHotelId) : "");
  const [hotelOptions, setHotelOptions] = useState<HotelOption[]>([]);
  const [name, setName] = useState("");
  const [otaUrls, setOtaUrls] = useState<OtaValues>(EMPTY_OTA_VALUES);
  const [platform, setPlatform] = useState<PlatformEnum>("Броневик");
  const [stars, setStars] = useState("");
  const [reviewRating, setReviewRating] = useState("");
  const [reviewCount, setReviewCount] = useState("");
  const [address, setAddress] = useState("");
  const [roomCount, setRoomCount] = useState("");
  const [formError, setFormError] = useState("");
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const filledOtaCount = useMemo(() => countFilledOtaUrls(otaUrls), [otaUrls]);
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60 disabled:opacity-60 disabled:cursor-not-allowed";

  const companyOptions = useMemo(() => companies.map((c) => ({
    label: c.company_name,
    value: String(c.id),
  })), [companies]);

  useEffect(() => {
    if (!open) {
      setCompanyId(defaultCompanyId ? String(defaultCompanyId) : "");
      setHotelId(defaultHotelId ? String(defaultHotelId) : "");
      setName("");
      setOtaUrls(EMPTY_OTA_VALUES);
      setPlatform("Броневик");
      setStars("");
      setReviewRating("");
      setReviewCount("");
      setAddress("");
      setRoomCount("");
      setFormError("");
      setLoadingHotels(false);
      setHotelOptions([]);
      setSubmitting(false);
      return;
    }

    if (defaultCompanyId) {
      setCompanyId(String(defaultCompanyId));
    }
    if (defaultHotelId) {
      setHotelId(String(defaultHotelId));
    }
  }, [open, defaultCompanyId, defaultHotelId]);

  useEffect(() => {
    if (!open || !companyId) {
      setHotelOptions([]);
      setHotelId("");
      return;
    }

    setLoadingHotels(true);
    setHotelId("");
    setHotelOptions([]);
    listCompanyHotels(Number(companyId))
      .then((data) => {
        const hotelsList = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        const options = hotelsList.map((h: any) => ({
          label: h.name,
          value: String(h.id),
        }));
        setHotelOptions(options);
        if (
          defaultHotelId &&
          String(defaultHotelId) &&
          options.some((opt: HotelOption) => opt.value === String(defaultHotelId))
        ) {
          setHotelId(String(defaultHotelId));
        }
      })
      .catch((e: any) => {
        setFormError(e?.response?.data?.detail || "Не удалось загрузить объекты компании");
      })
      .finally(() => setLoadingHotels(false));
  }, [open, companyId, defaultHotelId]);

  function handleOtaUrlChange(key: OtaKey, value: string) {
    setOtaUrls((current) => ({
      ...current,
      [key]: value,
    }));
    setFormError("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError("");

    if (!companyId) return setFormError("Выберите компанию");
    if (!hotelId) return setFormError("Выберите объект размещения");
    if (!name.trim()) return setFormError("Введите название конкурента");
    if (getInvalidOtaKeys(otaUrls).size > 0) {
      return setFormError("Проверьте ссылки конкурента: одна или несколько ссылок не соответствуют примеру");
    }
    if (filledOtaCount === 0) {
      return setFormError("Добавьте хотя бы одну OTA-ссылку для конкурента");
    }

    const otaUrlsPayload = buildOtaUrlsPayload(otaUrls);

    const payload: CompetitorCreateInput = {
      name: name.trim(),
      platform,
      url: otaUrlsPayload.official_website ?? Object.values(otaUrlsPayload)[0] ?? null,
      ota_urls: otaUrlsPayload,
      stars: stars !== "" ? Number(stars) : null,
      review_rating: reviewRating !== "" ? Number(reviewRating) : null,
      review_count: reviewCount !== "" ? Number(reviewCount) : null,
      address: address.trim() || null,
      room_count: roomCount !== "" ? Number(roomCount) : null,
    };

    const { stars: starsValue, review_rating: ratingValue, review_count: countValue, room_count: roomCountValue } = payload;

    if (starsValue !== null && starsValue !== undefined && (starsValue < 0 || starsValue > 5)) {
      return setFormError("Количество звёзд должно быть от 0 до 5");
    }
    if (ratingValue !== null && ratingValue !== undefined && (ratingValue < 0 || ratingValue > 10)) {
      return setFormError("Рейтинг отзывов должен быть в диапазоне 0-10");
    }
    if (countValue !== null && countValue !== undefined && countValue < 0) {
      return setFormError("Количество отзывов не может быть отрицательным");
    }
    if (roomCountValue !== null && roomCountValue !== undefined && roomCountValue < 0) {
      return setFormError("Количество номеров не может быть отрицательным");
    }

    setSubmitting(true);
    try {
      const created = await createCompetitor(Number(companyId), Number(hotelId), payload);
      onCreated(created);
      onClose();
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || "Не удалось добавить конкурента");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-3xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <UsersRound size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Добавить конкурента</h3>
            <p className="mt-1 text-sm text-slate-500">Заполните карточку, чтобы аналитика учитывала конкурента в отчётах</p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Компания *</span>
            <SelectField
              value={companyId}
              onChange={setCompanyId}
              options={companyOptions}
              placeholder="Выберите компанию"
              icon={<Building2 size={18} />}
              className="border-slate-200/80 bg-white/80 shadow-inner"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Объект размещения *</span>
            <SelectField
              value={hotelId}
              onChange={setHotelId}
              options={hotelOptions}
              placeholder={loadingHotels ? "Загрузка…" : "Выберите объект"}
              icon={<BedDouble size={18} />}
              className="border-slate-200/80 bg-white/80 shadow-inner"
              disabled={!companyId || loadingHotels}
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Название *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${baseInputClass}`}
              placeholder="Например, Hotel Plaza"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Платформа *</span>
            <SelectField
              value={platform}
              onChange={(value) => setPlatform(value as PlatformEnum)}
              options={PLATFORM_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
              placeholder="Выберите платформу"
              icon={<ClipboardList size={18} />}
              className="border-slate-200/80 bg-white/80 shadow-inner"
            />
          </label>

          <div className="md:col-span-2">
            <OtaUrlsFields
              values={otaUrls}
              onChange={handleOtaUrlChange}
              disabled={submitting}
              officialLabel="Сайт конкурента"
              title="Ссылки ОТА *"
            />
          </div>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Адрес</span>
            <div className="relative">
              <MapPin size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Город, улица"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Звёзды</span>
            <div className="relative">
              <Star size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={stars}
                onChange={(e) => setStars(e.target.value)}
                type="number"
                min={0}
                max={5}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="0-5"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Рейтинг отзывов</span>
            <div className="relative">
              <BarChart size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={reviewRating}
                onChange={(e) => setReviewRating(e.target.value)}
                type="number"
                step="0.1"
                min={0}
                max={10}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 8.4"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Количество отзывов</span>
            <div className="relative">
              <Hash size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={reviewCount}
                onChange={(e) => setReviewCount(e.target.value)}
                type="number"
                min={0}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 123"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Количество номеров</span>
            <div className="relative">
              <Hash size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={roomCount}
                onChange={(e) => setRoomCount(e.target.value)}
                type="number"
                min={0}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 50"
              />
            </div>
          </label>
        </div>

        {formError && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {formError}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Добавляем…" : "Добавить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
